import { takeEvery, take, takeLatest, put, call, select, fork, delay } from 'redux-saga/effects'

import { createDenormalizedSelector } from '../entities/selectors'
import * as sfproxy from '../sfproxy'
import * as grs from '../grs'
import getRandomness from '../shared/random-generator'
import * as searchDateTime from '../search-date-time'
import { locationsSelector } from '../sfproxy/selectors'

import * as api from './api'
import * as constants from './constants'
import * as actions from './actions'
import * as selectors from './selectors'
import { isBoundInBounds, getLocationsInBounds } from './helpers'

import { SEARCH_MODE_MONTHLY } from 'search/constants'

const sfParamsFromBounds = bounds => ({
    box: {
        first: {
            x: bounds.nw.lat,
            y: bounds.nw.lng,
        },
        second: {
            x: bounds.se.lat,
            y: bounds.se.lng,
        },
    },
})

const sfParamsFromFilters = filters => ({
    location: Object.keys(filters).reduce((result, value) => ((result[value] = filters[value]), result), {}),
})

export function* googleAPISaga() {
    let isFirstTime = true

    yield takeEvery(constants.MAP_LOAD_GOOGLE_API, function*() {
        const isApiReady = yield select(selectors.googleAPIReadySelector)

        if (!isApiReady && isFirstTime) {
            isFirstTime = false

            yield call(api.loadGoogleMapsAPI, {
                key: GOOGLE_MAPS_API_KEY,
                libraries: 'places',
            })

            yield put(actions.googleAPILoaded())
        }
    })
}

export function* watchBoundsSaga() {
    yield takeEvery(constants.SET_MAP_BOUNDS, function*() {
        const bounds = yield select(selectors.boundsSelector)

        if (bounds) yield put(actions.findMapLocations())
    })
}

export function* watchSearchModeSaga() {
    yield takeLatest(constants.SET_MAP_SEARCH_MODE, function*({ searchMode }) {
        if (SEARCH_MODE_MONTHLY === searchMode) {
            yield put(searchDateTime.adjustDateTimeRangeForSubscription())
            yield put(actions.findMapLocations())
        }
    })
}

export function* watchMapSearchDateTimeSaga() {
    yield takeLatest(constants.SET_MAP_SEARCH_DATETIME, function*({ start, end }) {
        yield delay(500)
        yield put(searchDateTime.setDateTimeRange(start, end))
        yield put(actions.findMapLocations())
    })
}

export function* watchTriggerSearchSaga() {
    yield takeEvery(constants.TRIGGER_SEARCH, function*() {
        yield put(actions.findMapLocations())
    })
}

const generateCriteria = (start, end, locations) =>
    locations
        .filter(location => location.grsLocation)
        .map(({ grsId }) => ({
            grsId,
            beginTime: start,
            endTime: end,
        }))

const locationRatesPredicate = (requestId, action) =>
    [grs.constants.RECEIVED_LOCATION_RATES, grs.constants.FAILED_TO_FETCH_RATES].indexOf(action.type) !== -1 &&
    action.requestId === requestId

export function* fetchRatesByCriteriaSaga(ratesCriteria) {
    if (ratesCriteria.length > 0) {
        const requestId = getRandomness()
        yield put(grs.actions.fetchRates(ratesCriteria, requestId))

        const { result, type, error } = yield take(locationRatesPredicate.bind(null, requestId))
        yield put(actions.setRates(type === grs.constants.RECEIVED_LOCATION_RATES ? result : error))
    }
}

export function* findLocationsSaga({ params = {} }) {
    yield delay(400)

    const maxMapBounds = yield select(selectors.maximumMapBoundsSelector)
    const locationsEntities = yield select(locationsSelector)

    yield put(actions.setRates(null))
    const requestId = getRandomness()
    const {
        searchDateTime: { start, end },
        bounds,
        filters,
    } = yield select(selectors.searchParamsSelector)

    let locationsInBounds
    if (
        !!Object.keys(locationsEntities).length &&
        isBoundInBounds(bounds, maxMapBounds) &&
        !Object.keys(filters).length
    ) {
        locationsInBounds = getLocationsInBounds(locationsEntities, bounds)
    } else {
        yield put(actions.setMaximumMapBounds(bounds))
        yield put(
            sfproxy.actions.fetchLocations(
                {
                    ...sfParamsFromBounds(bounds),
                    ...sfParamsFromFilters(filters),
                    ...params,
                },
                undefined,
                requestId
            )
        )

        const {
            result: { content },
        } = yield take(action => action.type === sfproxy.constants.RECEIVED_LOCATIONS && action.requestId === requestId)
        locationsInBounds = content
    }

    yield put(
        sfproxy.actions.fetchPlacesOfInterests(
            {
                ...sfParamsFromBounds(bounds),
                ...sfParamsFromFilters(filters),
                ...params,
            },
            requestId
        )
    )

    const { result: placesOfInterest } = yield take(
        action => action.type === sfproxy.constants.RECEIVED_PLACES_OF_INTEREST && action.requestId === requestId
    )

    yield put(actions.receivedMapResults(locationsInBounds, locationsInBounds.length, placesOfInterest))

    const locations = yield select(createDenormalizedSelector(locationsInBounds, [sfproxy.schemas.locationSchema]))
    const ratesCriteria = generateCriteria(start, end, locations)

    yield call(fetchRatesByCriteriaSaga, ratesCriteria)
}

export function* watchFindLocationsSaga() {
    yield takeLatest(constants.FIND_MAP_LOCATIONS, findLocationsSaga)
}

export function* rootSaga() {
    yield fork(googleAPISaga)
    yield fork(watchBoundsSaga)
    yield fork(watchSearchModeSaga)
    yield fork(watchMapSearchDateTimeSaga)
    yield fork(watchTriggerSearchSaga)
    yield fork(watchFindLocationsSaga)
}
